/**
 * @file Supabase client singleton.
 *
 * Auto-generated scaffolding -- extended with auth helpers.
 * Reads VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY (or ANON_KEY)
 * from environment variables.
 */
import { createClient } from '@supabase/supabase-js';
import { logger } from '@/shared/utils/logger';
import type { Database } from './types';
// Supporta anche un path relativo (es. /supabase-proxy per lo stack locale
// via proxy vite): risolto contro l'origine corrente, cosi' resta same-origin
// sia da localhost che dall'IP di rete. createClient esige un URL assoluto.
const RAW_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_URL = RAW_SUPABASE_URL?.startsWith('/')
  ? new URL(RAW_SUPABASE_URL, window.location.origin).toString().replace(/\/$/, '')
  : RAW_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

logger.debug('🔍 Supabase ENV check:', {
  url: SUPABASE_URL,
  hasKey: !!SUPABASE_PUBLISHABLE_KEY,
  keyLength: SUPABASE_PUBLISHABLE_KEY?.length,
  envKeys: Object.keys(import.meta.env).filter(k => k.startsWith('VITE_'))
});

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

/**
 * Pre-configured Supabase client instance.
 *
 * Usage: `import { supabase } from "@/integrations/supabase/client";`
 *
 * Configured with PKCE auth flow, auto token refresh, and localStorage persistence.
 */
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    storage: localStorage,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
});

// When running locally, route edge function calls to Supabase Cloud.
// Supports relative paths too (e.g. /functions-proxy for the local-stack
// vite proxy): resolved against the current origin.
const FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
if (FUNCTIONS_URL) {
  (supabase as any).functionsUrl = new URL(FUNCTIONS_URL, window.location.origin);
}

/**
 * Ensures the current Supabase session is valid.
 *
 * If no session exists or it is about to expire (within 5 minutes),
 * the token is refreshed automatically. Throws if refresh fails.
 *
 * @returns The current (or freshly refreshed) Supabase session.
 */
export const ensureValidSession = async () => {
  // First try to get the current session
  let { data: { session }, error: sessionError } = await supabase.auth.getSession();

  // If there's an error getting the session, try to refresh
  if (sessionError || !session) {
    logger.debug('🔄 ensureValidSession: No valid session found, attempting refresh');
    const { data: { session: refreshedSession }, error: refreshError } =
      await supabase.auth.refreshSession();

    if (refreshError || !refreshedSession) {
      console.error('❌ ensureValidSession: Failed to refresh session', refreshError);
      throw new Error('Utente non autenticato. Effettua nuovamente il login.');
    }

    session = refreshedSession;
  }

  // Check if token is about to expire (within 5 minutes)
  const expiresAt = session?.expires_at || 0;
  const now = Date.now();
  const expiresIn = expiresAt * 1000 - now;
  const isExpiringSoon = expiresIn < 300000; // 5 minutes

  logger.debug('🕒 ensureValidSession: Token status', {
    expiresAt: new Date(expiresAt * 1000).toLocaleString(),
    expiresIn: Math.floor(expiresIn / 1000) + ' seconds',
    isExpiringSoon
  });

  if (isExpiringSoon) {
    logger.debug('⚠️ ensureValidSession: Token expiring soon, refreshing');
    const { data: { session: refreshedSession }, error: refreshError } =
      await supabase.auth.refreshSession();

    if (refreshError || !refreshedSession) {
      console.error('❌ ensureValidSession: Failed to refresh expiring session', refreshError);
      throw new Error('Impossibile rinnovare la sessione');
    }

    return refreshedSession;
  }

  return session;
};
